import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';
import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const INVENTORY_RELIABILITY_DATA_SCRIPT_QUERY_KEY =
  'inventory-reliability-data-script';

const getReliabilityDataScriptQueryKey = (organizationId?: string) =>
  [INVENTORY_RELIABILITY_DATA_SCRIPT_QUERY_KEY, organizationId] as const;

const getReliabilityDataScriptQueryOptions = ({
  organizationId,
  enabled = true,
}: {
  organizationId?: string;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getReliabilityDataScriptQueryKey(organizationId),
    queryFn: async () => {
      const { data: commandData } =
        await apiClient.inventory.getReliabilityDataCommand({
          organizationId: organizationId!,
        });

      return { script: commandData?.command };
    },
    enabled: enabled && !!organizationId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

type QueryArgs = {
  enabled?: boolean;
};

export const useReliabilityDataScriptQuery = ({
  enabled = true,
}: QueryArgs = {}) => {
  const { currentOrganization } = useOrganizations();
  const currentOrganizationId = useCurrentOrganizationId();
  const organizationId = currentOrganization?.id ?? currentOrganizationId;

  const { isLoading, isFetching, data, error, refetch, isFetched } = useQuery(
    getReliabilityDataScriptQueryOptions({ organizationId, enabled })
  );

  const resolvedError =
    error ??
    (isFetched && !data?.script
      ? new Error('Snapshot script is empty')
      : undefined);

  return {
    isLoading: isLoading || isFetching,
    script: data?.script,
    error: resolvedError,
    refetch,
  };
};
